/**
 * TechnologyDomain Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedTextOrEmpty } from './shared';

/**
 * Common validation rules for TechnologyDomain entities
 */
export const technologyDomainValidationRules = {
  /** TechnologyDomain name validation (1-200 characters) */
  name: nameField(200, 'Name is required', 'Name must be less than 200 characters'),

  /** Description validation (optional, max 2000 characters) */
  description: optionalBoundedTextOrEmpty(2000, 'Description must be less than 2000 characters'),

  /** Notes validation (optional, max 5000 characters) */
  notes: optionalBoundedTextOrEmpty(5000, 'Notes must be less than 5000 characters'),
};

/**
 * Full TechnologyDomain create form schema
 */
export const createTechnologyDomainSchema = z.object({
  name: technologyDomainValidationRules.name,
  description: technologyDomainValidationRules.description,
  tags: z.array(z.string().min(1)).optional().default([]),
  notes: technologyDomainValidationRules.notes,
});

/**
 * TechnologyDomain update form schema (at least one field must be present)
 */
export const updateTechnologyDomainSchema = z
  .object({
    name: technologyDomainValidationRules.name.optional(),
    description: technologyDomainValidationRules.description,
    tags: z.array(z.string().min(1)).optional(),
    notes: technologyDomainValidationRules.notes,
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create technology domain form
 */
export type CreateTechnologyDomainFormData = z.infer<typeof createTechnologyDomainSchema>;

/**
 * Type inference for update technology domain form
 */
export type UpdateTechnologyDomainFormData = z.infer<typeof updateTechnologyDomainSchema>;
